import React, { useState, useEffect, useCallback } from 'react';
// --- AJUSTE: Se importa Alert ---
import { Table, Button, Space, message, Popconfirm, Tooltip, Alert } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { adminService } from '../../services/api';
import RoleForm from './RoleForm';
import { useAuth } from '../../context/AuthContext';

const RolesTab = () => {
    const { user } = useAuth();
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRole, setEditingRole] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false); 
    const [deleteError, setDeleteError] = useState(null); // --- AJUSTE: Estado para el mensaje de error

    const canCreateRole = user?.role?.profile?.permissions?.actions?.userManagement?.canCreateRole;
    const canEditRole = user?.role?.profile?.permissions?.actions?.userManagement?.canEditRole;
    const canDeleteRole = user?.role?.profile?.permissions?.actions?.userManagement?.canDeleteRole;

    const fetchRoles = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await adminService.getRoles();
            if (data.success) setRoles(data.data);
        } catch (error) {
            console.error('Fallo al cargar roles:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchRoles();
    }, [fetchRoles]);

    const handleCreate = () => {
        setDeleteError(null); // --- AJUSTE: Limpiar error al realizar otra acción
        setEditingRole(null);
        setIsModalOpen(true);
    };

    const handleEdit = (role) => {
        setDeleteError(null); // --- AJUSTE: Limpiar error al realizar otra acción
        setEditingRole(role);
        setIsModalOpen(true);
    };

    const handleDelete = async (roleId) => {
        setDeleteError(null); // Limpiar errores anteriores
        try {
            const response = await adminService.deleteRole(roleId);
            if (response.data.success) {
                message.success('Rol eliminado correctamente.');
                fetchRoles();
            }
        } catch (error) {
            // --- AJUSTE: Se establece el error en el estado para mostrar la Alerta
            const errorMessage = error.response?.data?.message || 'No se pudo eliminar el rol. Intente de nuevo.';
            setDeleteError(errorMessage);
            console.error('Fallo al eliminar rol:', error);
        }
    };

    const handleFormFinish = async (values) => {
        setIsSubmitting(true);
        try {
            if (editingRole) {
                await adminService.updateRole(editingRole._id, values);
                message.success('Rol actualizado correctamente.');
            } else {
                await adminService.createRole(values);
                message.success('Rol creado correctamente.');
            }
            setIsModalOpen(false);
            setEditingRole(null);
            fetchRoles();
        } catch (error) {
            console.error('Fallo en el formulario de rol:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const columns = [
        { title: 'Nombre del Rol', dataIndex: 'name', key: 'name' },
        { title: 'Descripción', dataIndex: 'description', key: 'description' },
        { title: 'Perfil de Permisos', dataIndex: 'profile', key: 'profile', render: profile => profile ? profile.name : 'Sin perfil' },
        {
            title: 'Acciones',
            key: 'actions',
            render: (_, record) => (
                <Space size="middle">
                    {canEditRole && (
                        <Tooltip title="Editar"><Button type="primary" icon={<EditOutlined />} onClick={() => handleEdit(record)} /></Tooltip>
                    )}
                    {canDeleteRole && (
                        <Tooltip title="Eliminar"><Popconfirm title="¿Está seguro de eliminar este rol?" onConfirm={() => handleDelete(record._id)} okText="Sí" cancelText="No"><Button type="primary" danger icon={<DeleteOutlined />} /></Popconfirm></Tooltip>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div>
            {/* --- AJUSTE: Se renderiza la Alerta si existe un error --- */}
            {deleteError && (
                <Alert
                    message="Error de Eliminación"
                    description={deleteError}
                    type="error"
                    showIcon
                    closable
                    onClose={() => setDeleteError(null)}
                    style={{ marginBottom: 16 }}
                />
            )}
            {canCreateRole && (
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate} style={{ marginBottom: 16 }}>
                    Crear Rol
                </Button>
            )}
            <Table columns={columns} dataSource={roles} loading={loading} rowKey="_id" bordered />
            <RoleForm
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                onFinish={handleFormFinish}
                initialValues={editingRole}
                submitting={isSubmitting}
            />
        </div>
    );
};

export default RolesTab;